/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import { createSelector } from 'reselect';

import { getFeatureFlags } from '../../../Checkout/ducks/Global/Global-Selectors';
import { getFeatureFlag } from '../App/App-Selectors';
import { getCartAttributeByNameAndReturnValue } from '../../../../../app/helpers/page/pageHelpers';
import { getCountryDetailsByThreeDigitCountryCode } from '../../../Checkout/helpers/countryCode';
import { propertiesTypeByProduct } from '../../../../../app/pages/Checkout/containers/EnterpriseCheckout/Common/Helpers/Sku-helper';

const getOrder = (state) => state?.order;
export const getOrderId = (state) => state?.order?.orderId || '';

export const getBillingAddress = (state) => state?.mfeCheckout?.payment?.billingAddress;

export const getCreateAccountStatus = (state) => state?.mfeCheckout?.createAccountStatus;

// TODO: Organise Errors for this
export const getPaymentError = (state) => state?.common?.error?.payment || {};
export const getOrderItemsWithError = (state) => {
    const paymentErrors = getPaymentError(state);
    return Object.keys(paymentErrors).filter((errorKey) => (Number(errorKey) > 0 && Number(errorKey) > 0 && paymentErrors[errorKey]));
};

const getFullCart = (state) => state?.mfeCheckout?.cart || null;
export const getCartOrder = createSelector(
    [getFullCart],
    (cart) => {
        // verified cart is not empty
        if (cart && cart.cartId) {
            return cart;
        }
        return null;
    },
);

export const getDetailedShipping = (state) => {
    const fdorder = getCartOrder(state);

    return fdorder?.detailedShipping || {};
};

export const getDetailedTaxes = (state) => {
    const fdorder = getCartOrder(state);

    return fdorder?.detailedTaxes || {};
};

export const getFuelSurCharges = (state) => {
    const expirementMode = getFeatureFlag('site-experiment-mode')(state);
    const detailShippingCharges = getDetailedShipping(state);
    const fuelSurChargedId = expirementMode?.metadata?.surChargeId || '';

    let serviceSurCharges = [];
    let shippingSurCharges = [];
    let serviceFuelCharges = 0;
    let shippingFuelCharges = 0;

    if (detailShippingCharges?.serviceCharge?.surcharges?.length > 0) {
        serviceSurCharges = detailShippingCharges?.serviceCharge?.surcharges;
    }

    if (detailShippingCharges?.shippingCharge?.surcharges?.length > 0) {
        shippingSurCharges = detailShippingCharges?.shippingCharge?.surcharges;
    }

    serviceSurCharges.forEach((charges) => {
        const surChargeId = charges?.surchargeId || '';

        if (surChargeId === fuelSurChargedId) {
            serviceFuelCharges = charges.amount || 0;
        }
    });

    shippingSurCharges.forEach((charges) => {
        const surChargeId = charges?.surchargeId || '';

        if (surChargeId === fuelSurChargedId) {
            shippingFuelCharges = charges.amount || 0;
        }
    });

    if (serviceFuelCharges || shippingFuelCharges) {
        return parseFloat((Number(serviceFuelCharges || 0) + Number(shippingFuelCharges || 0))).toFixed(2);
    }
    return null;
};

export const getStateDeliveryFee = (state) => {
    const detailShippingCharges = getDetailedShipping(state);

    let serviceSurCharges = [];
    let serviceDeliveryFee = null;

    if (detailShippingCharges?.serviceCharge?.surcharges?.length > 0) {
        serviceSurCharges = detailShippingCharges?.serviceCharge?.surcharges;
    }

    serviceSurCharges.forEach((charges) => {
        const surchargeId = charges?.surchargeId || '';

        if (surchargeId === '4') {
            serviceDeliveryFee = parseFloat(charges.amount).toFixed(2);
        }
    });

    let shippingSurCharges = [];
    let shippingDeliveryFee = null;

    if (detailShippingCharges?.shippingCharge?.surcharges?.length > 0) {
        shippingSurCharges = detailShippingCharges?.shippingCharge?.surcharges;
    }

    shippingSurCharges.forEach((charges) => {
        const surchargeId = charges?.surchargeId || '';

        if (surchargeId === '4') {
            shippingDeliveryFee = parseFloat(charges.amount).toFixed(2);
        }
    });

    if (serviceDeliveryFee || shippingDeliveryFee) {
        return (Number(serviceDeliveryFee) + Number(shippingDeliveryFee));
    }

    return null;
};

export const getRegulatoryFees = (state) => {
    const detailShippingCharges = getDetailedShipping(state);
    const detailTaxesCharges = getDetailedTaxes(state);

    const regulatoryFeeKeys = [];
    let regulatoryTotalFees = 0;

    let serviceSurCharges = [];
    let shippingSurCharges = [];
    let taxesSurCharges = [];

    if (detailShippingCharges?.serviceCharge?.surcharges?.length > 0) {
        serviceSurCharges = detailShippingCharges?.serviceCharge?.surcharges;
        regulatoryTotalFees += Number(detailShippingCharges?.serviceCharge?.regulatoryFee || 0);

        // Get the key to geet content stack
        serviceSurCharges.forEach((charges) => {
            if (charges.display && charges?.surchargeId && !regulatoryFeeKeys.includes(charges?.surchargeId)) {
                regulatoryFeeKeys.push(charges?.surchargeId);
            }
        });
    }

    if (detailShippingCharges?.shippingCharge?.surcharges?.length > 0) {
        shippingSurCharges = detailShippingCharges?.shippingCharge?.surcharges;
        regulatoryTotalFees += Number(detailShippingCharges?.shippingCharge?.regulatoryFee || 0);

        // Get the key to geet content stack
        shippingSurCharges.forEach((charges) => {
            if (charges.display && charges?.surchargeId && !regulatoryFeeKeys.includes(charges?.surchargeId)) {
                regulatoryFeeKeys.push(charges?.surchargeId);
            }
        });
    }

    if (Number(detailTaxesCharges?.regulatoryFee) > 0) {
        taxesSurCharges = Number(detailTaxesCharges?.regulatoryFee);
        regulatoryFeeKeys.push('bottlefee');
        regulatoryTotalFees += Number(taxesSurCharges);
    }

    return {
        regulatoryFeeKeys,
        regulatoryTotalFees: parseFloat(regulatoryTotalFees).toFixed(2),
    };
};

const getOrderTotalAmount = createSelector(
    [getCartOrder], (cart) => ({ orderTotal: cart?.orderTotalBalance }),
);

export const getAmount = createSelector([getOrderTotalAmount], (amount) => amount);

export const getGiftCards = (state) => getCartOrder(state)?.appliedGiftCards;

export const getOrderSummary = (state) => getCartOrder(state)?.orderSummary || [];
export const getOrderTotalProduct = (state) => getCartOrder(state)?.totalProduct || 0;
export const getOrderTotalTax = (state) => getCartOrder(state)?.detailedTaxes?.taxesWithoutRegulatoryFee || 0;
export const getFirstItemRecipientZipCode = (state) => getCartOrder(state)?.recipients?.[0]?.recipientAddress?.zipCode || '';
export const getOrderConfirmationId = (state) => getCartOrder(state)?.orderIdWithSourceId || '';
export const getFirstItemBillingZipCode = (state) => getCartOrder(state)?.customer?.customerAddress?.zipCode || '';

export const getAppliedPromotions = (state) => getCartOrder(state)?.appliedPromotions || [];
export const getAppliedGiftCards = (state) => getCartOrder(state)?.appliedGiftCards || [];

const getGiftCardDiscounts = (state) => getCartOrder(state)?.totalGiftCardAmount || '0.00';
export const getTotalAdjustments = (state) => getCartOrder(state)?.totalDiscount || 0;
export const getTotalRewardAmount = (state) => getCartOrder(state)?.totalRewardAmount || 0;

const getorderTotalBeforeTaxes = (state) => getCartOrder(state)?.totalBeforeTaxes || 0;
const getTotalShippingAdjustments = (state) => getCartOrder(state)?.totalNetShippingDiscount || 0;
export const getRecipientOrderItems = (state) => getCartOrder(state)?.recipients;

export const getExternalCartOrderTotal = (state) => getCartOrder(state)?.externalCart?.orderTotal || 0;
export const getExternalCartStatus = (state) => getCartOrder(state)?.externalCart?.status;

const countNoOfOrderItems = (orderItems) => {
    let noOfItems = 0;
    Object.keys(orderItems).forEach((index) => {
        noOfItems += orderItems[index].cartItems.length;
    });
    return noOfItems;
};

// Get Part number of all order items
export const getPartNumberOfOrderItems = (state) => {
    const recipients = getRecipientOrderItems(state);
    const partNumbers = [];
    if (recipients?.length > 0) {
        // eslint-disable-next-line array-callback-return
        recipients.map((recipient) => {
            const cartItems = recipient?.cartItems;
            if (cartItems?.length > 0) {
                // eslint-disable-next-line array-callback-return
                cartItems.map((orderItem) => {
                    const partNumber = orderItem?.product.parentProductCode || orderItem?.product.productCode;
                    partNumbers.push(partNumber);
                });
            }
        });
    }
    return partNumbers;
};

// Get Order Item By Order Id
export const getOrderItemById = (orderItemList, cartItemId) => {
    const filterOrderItem = [];
    if (orderItemList.length > 0) {
        orderItemList.forEach((items) => {
            items.cartItems.forEach((cartItem) => {
                if (cartItem.cartItemId === cartItemId) {
                    const { recipientAddress } = items;
                    filterOrderItem[0] = {
                        cartItem: [cartItem],
                        recipientAddress,
                    };
                }
            });
        });
    }
    return filterOrderItem;
};

export const getBillingFromCart = (state) => {
    const cart = getCartOrder(state);
    if (cart?.customer?.customerAddress) {
        const billingAddress = cart.customer.customerAddress;
        let country = billingAddress.country;
        if (billingAddress.country?.length === 3) {
            const countryItem = getCountryDetailsByThreeDigitCountryCode(billingAddress.country);
            country = countryItem?.countryCodeTwoDigit;
        }

        return {
            firstName: billingAddress.firstName || '',
            lastName: billingAddress.lastName || '',
            address1: billingAddress.address1 || '',
            address2: billingAddress.address2 || '',
            state: billingAddress.state || '',
            country: country || 'US',
            zipCode: billingAddress.zipCode || '',
            city: billingAddress.city || '',
            phone: billingAddress.phoneNumber || '',
            email: billingAddress.email || '',
            confirmEmail: billingAddress.email || '',
        };
    }

    return {};
};

// Exclude Passport Item from the cart
export const getOrderItemExcludePassportAndDonation = createSelector(
    [
        getRecipientOrderItems,
    ],
    (recipients) => {
        if (recipients) {
            return recipients.filter((items) => (
                items.cartItems.filter((cartItem) => !cartItem.product.isPassport
                    && cartItem.itemInfo?.lineItemType !== 'DONATION').length !== 0
            ));
        }
        return [];
    },
);

// Get Passport Item from the cart
export const getPassportItem = (state) => {
    const cart = getCartOrder(state);
    if (cart?.recipients) {
        const { recipients, cartId } = cart;
        if (recipients?.length > 0) {
            const passportItem = recipients.filter((items) => (
                items.cartItems.filter((cartItem) => !!cartItem.product.isPassport).length !== 0
            ));

            if (passportItem && passportItem.length > 0) {
                passportItem[0].ordersId = cartId;
            }

            return passportItem;
        }
    }
    return [];
};

export const getRecipientsExcludePassport = createSelector(
    [
        getOrder,
        getOrderId,
        getFullCart,
        getOrderItemExcludePassportAndDonation,
    ],
    (order, orderId, cartOrder, excudePassportItem) => {
        let recips = [];
        try {
            if (!order && !orderId) {
                return [];
            }
            if (cartOrder?.recipients) {
                recips = excudePassportItem;
            }
        } catch (ex) {
            console.error('ERROR: getRecipients selector FAILED', ex);
        }
        return recips;
    },
);

export const getIsPassportOnlyItemOnCart = (state) => {
    const passportItem = getPassportItem(state);
    const cartItems = getRecipientsExcludePassport(state);
    if (passportItem.length > 0 && cartItems && cartItems.length === 0) {
        return true;
    }
    return false;
};

export const getOrderProductTypes = createSelector(
    [
        getOrderItemExcludePassportAndDonation,
        getIsPassportOnlyItemOnCart,
    ],
    (recipients, isPassportOnlyItem) => {
        let hasFPTProduct = false;
        let hasGPTProduct = false;
        let hasFlowerProducts = false;
        let hasFoodProducts = false;

        if (recipients.length > 0) {
            recipients.forEach((items) => {
                items.cartItems.forEach((item) => {
                    if (item.product) {
                        if (item.product.productType === 'FPT') {
                            hasFPTProduct = true;
                        }
                        if (item.product.productType === 'GPT') {
                            hasGPTProduct = true;
                        }

                        if (propertiesTypeByProduct[item.product.brandCode] === 'flower') {
                            hasFlowerProducts = true;
                        }

                        if (propertiesTypeByProduct[item.product.brandCode] === 'food') {
                            hasFoodProducts = true;
                        }
                    }
                });
            });
        }
        if (isPassportOnlyItem) {
            hasGPTProduct = true;
        }
        return {
            hasFPTProduct,
            hasGPTProduct,
            hasFlowerProducts,
            hasFoodProducts,
        };
    });

export const getHasMultiBrandProduct = (state) => {
    const { hasFPTProduct, hasGPTProduct } = getOrderProductTypes(state);
    if (hasFPTProduct && hasGPTProduct) {
        return true;
    }
    return false;
};

export const getHasMultiFamilyProducts = (state) => {
    const { hasFlowerProducts, hasFoodProducts } = getOrderProductTypes(state);
    if (hasFlowerProducts && hasFoodProducts) {
        return true;
    }
    return false;
};

// List countries is in the recipient on cart
export const getOrderItemsCountries = (state) => {
    const countries = [];
    const cart = getCartOrder(state);
    if (cart?.recipients?.length > 0) {
        const { recipients } = cart || {};
        recipients.forEach((item) => {
            if (item.recipientAddress?.country) {
                countries.push(item.recipientAddress?.country);
            }
        });
    }
    return countries;
};

export const getHasInternationalProductInCart = createSelector([
    getOrderItemsCountries,
], (countries) => {
    const listInternational = countries.filter(
        (country) => !['US', 'USA', 'CAN', 'CA'].includes(country),
    );
    if (listInternational?.length > 0) {
        return true;
    }

    return false;
});

export const getOrderTotalBalance = (state) => state?.mfeCheckout?.cart.cartTotal || '0.00';

// List product by Order Item and name
export const getOrderItemAndName = (state) => {
    const itemsName = {};
    if (state.mfeCheckout?.cart?.recipients?.length > 0) {
        const { recipients } = state.mfeCheckout?.cart || {};
        recipients.filter((items) => (
            items.cartItems.filter((cartItem) => !cartItem.product?.isPassport && cartItem.itemInfo?.lineItemType !== 'DONATION').length !== 0
        )).forEach((items) => {
            items.cartItems.forEach((item) => {
                itemsName[item.cartItemId] = item.product?.parentProductName;
            });
        });
    }
    return itemsName;
};

export const getOrderContainsClubItemPayByCreditCard = createSelector([getCartOrder], (cart) => {
    let clubItem = [];

    // Club Items that have paymentMethod set with value 0 will not have recurring payment
    // Credit Card is the only payment method displayed to customer
    if (cart?.recipients) {
        const { recipients } = cart;
        clubItem = recipients.filter((recipient) => (
            recipient.cartItems.filter((item) => {
                let paymentMethodCode = -1;

                if (item.product?.paymentMethod && item.product.paymentMethod !== '') {
                    paymentMethodCode = item.product.paymentMethod;
                }

                if (Number(paymentMethodCode) === 0) {
                    return true;
                }

                return false;
            }).length !== 0
        ));
    }

    if (clubItem.length > 0) return true;

    return false;
});

export const getAllItemsExceptForDigitalProdDonationAndPassport = (state) => {
    const cart = getCartOrder(state);

    if (cart?.recipients) {
        const { recipients } = cart;
        return recipients.filter((items) => (
            items.cartItems.filter((cartItem) => (
                !cartItem.product.isPassport
                && (cartItem.itemInfo?.lineItemType !== 'DONATION'
                    || cartItem.itemInfo?.lineItemType !== 'NBCU'
                    || cartItem.itemInfo?.lineItemType !== 'DDELVR')
            )).length !== 0
        ));
    }
    return [];
};

export const getOrderContainsSubscriptionItem = createSelector([getCartOrder], (cart) => {
    if (cart?.recipients) {
        const { recipients } = cart;
        return recipients.filter((recipient) => (
            recipient.cartItems.filter((item) => item.subscription?.type
                || item.product?.seoUrl?.includes('subscription')).length !== 0
        ));
    }
    return [];
});

// Get the list of orderItem ids
export const getOrderItemIds = (state) => {
    const cartItemIds = [];
    const cart = getCartOrder(state);

    if (cart) {
        const { recipients } = cart;
        recipients.filter((items) => (
            items.cartItems.filter((cartItem) => !cartItem.product.isPassport && cartItem.itemInfo?.lineItemType !== 'DONATION').length !== 0
        )).forEach((items) => {
            items.cartItems.forEach((item) => {
                cartItemIds.push(item.cartItemId);
            });
        });
    }
    return cartItemIds;
};

export const getDonationItem = (state) => {
    const donationItem = [];
    const cart = getCartOrder(state);

    if (cart?.recipients) {
        const { recipients } = cart;
        recipients.forEach((items) => {
            items.cartItems.forEach((cartItem) => {
                if (cartItem.itemInfo?.lineItemType === 'DONATION') {
                    donationItem.push({
                        cartItems: [cartItem],
                        recipientAddress: items.recipientAddress,
                    });
                }
            });
        });
    }
    return donationItem;
};

export const getGiftCardDetails = (state) => getFullCart(state).appliedGiftCards || [];
export const getIfGiftCardApplied = (state) => {
    const giftCard = state.mfeCheckout?.cart?.appliedGiftCards || [];
    if (giftCard && giftCard.length > 0) {
        return true;
    }
    return false;
};

export const getRecipients = createSelector(
    [
        getOrder,
        getOrderId,
        getFullCart,
    ],
    (order, orderId, cartOrder) => {
        let recips = [];

        try {
            if (!order && !orderId) {
                return [];
            }
            if (cartOrder?.recipients) {
                recips = cartOrder.recipients;
            }
        } catch (ex) {
            console.error('ERROR: getRecipients selector FAILED', ex);
        }

        return recips;
    },
);

export const getCartOrderDetails = createSelector(
    [getFullCart],
    (cartOrder) => {
        try {
            if (cartOrder) {
                return cartOrder;
            }
        } catch (ex) {
            console.error('ERROR: getCartOrderDetails selector FAILED', ex);
        }

        return null;
    },
);

export const getNoOfOrderItems = (state) => {
    const cartItems = getRecipientsExcludePassport(state);
    if (cartItems && cartItems.length > 0) {
        return countNoOfOrderItems(cartItems);
    }
    return 0;
};

export const isDeliveryMessageAndDateFilledForAllItems = (state) => {
    const recipients = getOrderItemExcludePassportAndDonation(state);
    let isDelMsgAndDateFilled = true;
    if (recipients && recipients.length > 0) {
        recipients.forEach((orderItem) => {
            orderItem.cartItems.forEach((item) => {
                if (!item.greetingMessage?.greetingMessage || !item.deliveryInfo?.deliveryDate) {
                    isDelMsgAndDateFilled = false;

                    // if greeting message is not present but greeting card was added
                    if (!item.greetingMessage?.greetingMessage && item.greetingCards?.length > 0) {
                        isDelMsgAndDateFilled = true;
                    }
                }
            });
        });
    } else {
        isDelMsgAndDateFilled = false;
    }
    return isDelMsgAndDateFilled;
};

export const getIsSciItemInCart = (state) => {
    const recipients = getOrderItemExcludePassportAndDonation(state);

    let sciItemInCart = false;

    if (recipients && recipients.length > 0) {
        recipients.forEach((orderItem) => {
            orderItem.cartItems.forEach((item) => {
                if (item?.sympathy?.sympathySource === 'SCI') {
                    sciItemInCart = true;
                }
            });
        });
    }

    return sciItemInCart;
};

// Exclude Passport Item from the cart
export const isPassportInEligibleProductsInCart = (state) => {
    let isInEligible = false;
    const recipients = getOrderItemExcludePassportAndDonation(state);
    if (recipients && recipients.length > 0) {
        recipients.forEach((orderItem) => {
            orderItem.cartItems.forEach((item) => {
                if (!item.product?.isPassport) isInEligible = true;
            });
        });
    }
    return isInEligible;
};

export const getGreetingCards = (state) => {
    const cartRecipients = state.mfeCheckout?.cart?.recipients || [];
    const cartId = state.mfeCheckout?.cart?.cartId || null;

    const greetingCards = [];
    if (cartRecipients.length > 0) {
        cartRecipients.forEach((recipient) => {
            if (recipient?.cartItems?.length > 0) {
                recipient.cartItems.forEach((orderItem) => {
                    if (orderItem?.greetingCards?.length > 0) {
                        orderItem.greetingCards.forEach((card) => {
                            greetingCards.push({
                                keyId: card?.greetingCardKey,
                                ordersId: cartId,
                                orderItemsId: orderItem.cartItemId,
                                skuCode: card?.product?.productCode,
                            });
                        });
                    }
                });
            }
        });
    }
    return greetingCards;
};

export const getLabelData = (state) => {
    const cartOrder = state.mfeCheckout?.cart?.recipients || [];
    const cartId = state.mfeCheckout?.cart?.cartId || [];
    const labelData = [];

    if (cartOrder?.length > 0) {
        cartOrder.forEach((recipient) => {
            if (recipient?.cartItems?.length > 0) {
                recipient.cartItems.forEach((cartItem) => {
                    if (cartItem.addons?.length > 0) {
                        cartItem.addons.forEach((addon) => {
                            if (addon.attributes?.length > 0) {
                                const personalizationId = getCartAttributeByNameAndReturnValue(addon.attributes, 'key_id') || null;
                                if (personalizationId) {
                                    labelData.push({
                                        keyId: personalizationId,
                                        ordersId: cartId,
                                        orderItemsId: cartItem.cartItemId,
                                        skuCode: cartItem.product?.productCode,
                                    });
                                }
                            }
                        });
                    }
                });
            }
        });
    }
    return labelData;
};

// Review if there is HD(1019), SY(1018), WLF(1020) product in the cart.
export const isHDFamilyBrandProductInCart = createSelector(
    [getOrderItemExcludePassportAndDonation],
    (cartItems) => {
        let productAvailable = false;
        cartItems.forEach((items) => (
            items.cartItems.forEach(
                (item) => {
                    if (['1019', '1020', '1018'].includes(item.product.brandCode)) {
                        productAvailable = true;
                    }
                },
            )
        ));
        return productAvailable;
    });

export const getIsDeliveryAddressFilledForAllItems = (state) => {
    const recipients = getOrderItemExcludePassportAndDonation(state);
    const isServiceTimeRequired = getFeatureFlag('is-service-time-required-to-proceed-to-next-step')(state);
    let addressFilled = true;
    if (recipients && recipients.length > 0) {
        recipients.forEach((orderItem) => {
            const { recipientAddress, cartItems } = orderItem;
            const sympathy = cartItems?.[0]?.sympathy;
            const isSympathyObjectFilled = sympathy && sympathy?.viewStartTime && sympathy?.viewStartTime !== '';
            if (!recipientAddress?.firstName) {
                addressFilled = false;
            }
            if (
                isServiceTimeRequired
                && ['Funeral home', 'Church'].includes(recipientAddress?.locationType)
                && cartItems?.[0]?.product?.productType === 'FPT'
                && !isSympathyObjectFilled) {
                addressFilled = false;
            }
        });
    } else {
        addressFilled = false;
    }

    return addressFilled;
};

export const getIsContactInfoFilled = (state) => {
    const { email, phone } = state?.mfeCheckout?.payment?.billingAddressForm || {};
    return !!(email && phone);
};

export const safeGetOrderItemsId = (state) => {
    const cart = getCartOrder(state);

    if (cart?.recipients) {
        return cart.recipients[0].cartItems[0].cartItemId;
    }
    return '';
};

export const getPersonalizationPrice = (state) => {
    let totalPrice = 0;
    const totalProductPrice = getOrderTotalProduct(state);
    const { recipients } = getCartOrder(state);
    recipients.forEach((recipient) => {
        const { cartItems } = recipient;
        cartItems.forEach((order) => {
            totalPrice += order.itemInfo?.totalProduct;
        });
    });
    return parseFloat(totalProductPrice - totalPrice).toFixed(2);
};

export const getCartCount = (state) => {
    const cartOrder = getCartOrder(state);

    if (cartOrder) {
        const { recipients } = cartOrder;
        if (recipients?.length > 0) {
            return recipients.map((items) => (
                items.cartItems.length
            ))?.reduce((total, orderItem) => total + orderItem);
        }
    }
    return 0;
};

export const getAddOnCount = (state) => {
    const cartOrder = getCartOrder(state);
    let count = 0;
    if (cartOrder) {
        const { recipients } = cartOrder;
        if (recipients?.length > 0) {
            recipients.forEach((items) => {
                if (items.cartItems?.length > 0) {
                    items.cartItems.forEach((item) => {
                        if (item.addons?.length > 0) {
                            count += item.addons.length;
                        }
                    });
                }
            });
        }
    }
    return count;
};

// Get Movie Item from cart
export const getMovieItemInCart = createSelector(
    [getRecipientsExcludePassport],
    (recipients) => {
        if (recipients.length) {
            return recipients.filter((items) => (
                items.cartItems.filter((cartItem) => (
                    cartItem.itemInfo?.lineItemType === 'NBCU'
                )).length !== 0
            ));
        }
        return [];
    },
);

export const getOrderCharges = (state) => {
    const cartOrderData = getCartOrder(state);
    if (cartOrderData && (cartOrderData.totalNetShipping || cartOrderData.totalServiceCharge)) {
        return {
            shippingCharge: Number(cartOrderData.totalNetShipping),
            serviceCharge: Number(cartOrderData.totalServiceCharge),
        };
    }

    return {
        shippingCharge: 0,
        serviceCharge: 0,
    };
};

export const getDetailShippingCharges = (state) => {
    const detailShippingCharges = getDetailedShipping(state);

    const serviceCharge = detailShippingCharges?.serviceCharge?.serviceChargesWithoutRegulatoryFee;
    const shippingCharge = detailShippingCharges?.shippingCharge?.shippingChargesWithoutRegulatoryFee;

    const serviceDiscount = detailShippingCharges?.serviceCharge?.serviceChargeDiscount;
    const shippingDiscount = detailShippingCharges?.shippingCharge?.shippingDiscount;

    // this is not need be calculate anymore
    // if (detailShippingCharges?.serviceCharge?.baseCharges) {
    //     const {
    //         baseCharges = 0,
    //         upCharges = 0,
    //         dtwCharges = 0,
    //         surcharges = [],
    //     } = detailShippingCharges.serviceCharge || {};

    //     let totalSurcharge = 0;
    //     // Start add the lines in a group
    //     surcharges.forEach((charges) => {
    //         if (!charges.display) {
    //             totalSurcharge += Number(charges.amount) || 0;
    //         }
    //     });

    //     serviceCharge = (Number(baseCharges) + Number(upCharges) + Number(dtwCharges) + totalSurcharge) || 0;
    //     serviceCharge = parseFloat(serviceCharge).toFixed(2);
    // }

    // if (detailShippingCharges?.shippingCharge?.baseCharges) {
    //     const {
    //         baseCharges = 0,
    //         upCharges = 0,
    //         dtwCharges = 0,
    //         surcharges = [],
    //     } = detailShippingCharges.shippingCharge || {};

    //     let totalSurcharge = 0;
    //     // Start add the lines in a group
    //     surcharges.forEach((charges) => {
    //         if (!charges.display) {
    //             totalSurcharge += Number(charges.amount) || 0;
    //         }
    //     });

    //     shippingCharge = (Number(baseCharges) + Number(upCharges) + Number(dtwCharges) + totalSurcharge) || 0;
    //     shippingCharge = parseFloat(shippingCharge).toFixed(2);
    // }

    return {
        serviceCharge,
        shippingCharge,
        serviceDiscount,
        shippingDiscount,
    };
};

export const getContextDisplayCharges = (state) => state?.mfeCheckout?.cart?.context?.displayCharges;

export const getCartCampaings = (state) => state?.mfeCheckout?.cart?.campaigns || [];
export const getFullDetailShippingServiceCharges = (state) => state?.mfeCheckout?.cart?.detailedShipping || {};

export const getIsDonationOnlyItemOnCart = (state) => {
    const donationItem = getDonationItem(state);
    const passportItemInCart = getPassportItem(state);
    const cartItems = getRecipientsExcludePassport(state);
    if (donationItem.length > 0 && cartItems && cartItems.length === 0 && passportItemInCart.length === 0) {
        return true;
    }
    return false;
};

// Passport
export const getPassportSubscriptionStatus = createSelector([getPassportItem], (passportItem) => {
    if (passportItem.length > 0) {
        return true;
    }

    return false;
});

export const getDonationOnlyStatus = (state) => {
    const donationItem = getDonationItem(state);
    if (donationItem.length > 0) {
        return true;
    }
    return false;
};

export const getOrderStatus = (state) => {
    const cart = state?.mfeCheckout?.cart;
    if (state.order?.orderId && cart?.cartStatus) {
        return cart.cartStatus.toLowerCase();
    }
    return null;
};

export const getOrderTotalSummary = (state) => {
    const detailShippingCharges = getDetailShippingCharges(state);

    const shippingCharge = detailShippingCharges?.shippingCharge || 0;
    const serviceCharge = detailShippingCharges?.serviceCharge || 0;

    return ({
        totalProduct: getOrderTotalProduct(state),
        totalShipping: Number(shippingCharge),
        totalService: Number(serviceCharge),
        totalShippingAdjustments: getTotalShippingAdjustments(state),
        totalAdjustments: getTotalAdjustments(state),
        totalTax: (Number(getOrderTotalTax(state))).toFixed(2),
        orderTotalBalance: getOrderTotalBalance(state),
        totalGiftCards: Number(getGiftCardDiscounts(state)).toFixed(2),
        orderTotalBeforeTaxes: getorderTotalBeforeTaxes(state),
        externalCartTotal: getExternalCartOrderTotal(state),
        totalRewardAmount: getTotalRewardAmount(state),
    });
};

export const getOrderTotalShipping = (state) => {
    const cartOrder = state?.mfeCheckout?.cart;
    if (cartOrder && cartOrder.totalShipping) {
        return Number(cartOrder.totalShipping).toFixed(2);
    }
    return 0;
};

export const getShopRunnerEligibleItemInCart = (state) => {
    let shopRunnerEligbleItem = [];
    const cart = getCartOrder(state);

    if (cart) {
        const { recipients } = cart;
        shopRunnerEligbleItem = recipients.filter((items) => (
            items.cartItems.filter((cartItems) => (cartItems.product.isPassport && cartItems.product.isShoprunnerEligible) || cartItems.product.isShoprunnerEligible).length !== 0
        ));
    }
    if (shopRunnerEligbleItem.length > 0) {
        return true;
    }
    return false;
};

// Check Digital item in cart
export const getDigitalItemInCart = createSelector(
    [getMovieItemInCart, getPassportItem],
    (movieItems, passportItem) => {
        let hasDigitalItemInCart = false;

        if (movieItems?.length > 0 || passportItem?.length > 0) {
            hasDigitalItemInCart = true;
        }

        return hasDigitalItemInCart;
    },
);

export const getLastAddedProductExcludePassportAndDonation = (state) => {
    const featureFlags = getFeatureFlags(state);
    const cart = getCartOrder(state);

    if (cart?.recipients) {
        const { recipients } = cart;
        const arrRegularItems = recipients.filter((items) => (
            items.cartItems.filter((cartItem) => !cartItem.product.isPassport && cartItem.itemInfo?.lineItemType !== 'DONATION').length !== 0
        ));

        if (featureFlags['is-cart-reverse-enabled']) {
            return arrRegularItems[arrRegularItems.length - 1] || {};
        }

        return arrRegularItems[0] || {};
    }

    return {};
};

// -----------------------------------------------
// Excludes Promo From the list
export const getOrderItemsExcludePromo = (state) => {
    const cart = getCartOrder(state);
    if (cart) {
        const { recipients } = cart;
        return recipients.filter((items) => (
            items.cartItems.filter((cartItem) => !cartItem.product.isPassport && cartItem.itemInfo?.lineItemType !== 'DONATION').length !== 0
        ));
    }
    return [];
};

export const getCurrentViewOrderItem = (state) => {
    const recipients = getOrderItemsExcludePromo(state);
    if (recipients.length) {
        return recipients[0];
    }
    return {};
};

export const getCartExternalCartItems = createSelector(
    [getCartOrder],
    (cart) => {
        let cartExternalCartItems = {};
        if (cart?.externalCart) {
            cartExternalCartItems = cart.externalCart;
        }

        return cartExternalCartItems;
    },
);

// Exclude Passport, Donation and digital products Item from the cart
export const getOrderItemExcludeSpecialProducts = createSelector(
    [
        getRecipientOrderItems,
    ],
    (recipients) => {
        if (recipients) {
            return recipients.filter((items) => (
                items.cartItems.filter(
                    (cartItem) => !cartItem.product.isPassport
                        && !['DONATION', 'NBCU', 'DDELVR'].includes(cartItem.itemInfo?.lineItemType),
                ).length !== 0
            ));
        }
        return [];
    },
);

export const isPassportInCart = (state) => {
    let passportItem = [];
    if (state?.mfeCheckout?.cart?.recipients) {
        passportItem = state.mfeCheckout.cart.recipients.filter((items) => (
            items.cartItems.filter(
                (cartItem) => cartItem.product.isPassport,
            ).length !== 0
        ));
    }

    return passportItem?.length > 0;
};

// Get only Digital products Item from the cart NBCU, DDELVR
export const getHasOrderItemDigitalProducts = createSelector(
    [
        getRecipientOrderItems,
    ],
    (recipients) => {
        let digitalProducts = [];
        if (recipients) {
            digitalProducts = recipients.filter((items) => (
                items.cartItems.filter(
                    (cartItem) => ['NBCU', 'DDELVR'].includes(cartItem.itemInfo?.lineItemType),
                ).length !== 0
            ));
        }
        if (digitalProducts?.length > 0) {
            return true;
        }
        return false;
    },
);

// Get only Greeting card standalone products Item from the cart lineItemType 'GREETINGCARD-ITEM'
export const getHasGreetingCardStandaloneProducts = createSelector(
    [
        getRecipientOrderItems,
    ],
    (recipients) => {
        let standaloneProducts = [];
        if (recipients) {
            standaloneProducts = recipients.filter((items) => (
                items.cartItems.filter(
                    (cartItem) => (
                        ['GREETINGCARD-ITEM'].includes(cartItem.itemInfo?.lineItemType)
                        || cartItem.product?.brandCode === '1036'
                    ),
                ).length !== 0
            ));
        }
        return standaloneProducts?.length > 0;
    },
);

export const getIsSympathyProductInCart = (state) => {
    let sympathyProducts = [];
    const cart = getCartOrder(state);

    if (cart) {
        const { recipients } = cart;
        sympathyProducts = recipients.filter((items) => items?.cartItems?.find((cartItem) => ['1', '2'].includes(cartItem?.product?.sympathyValue)));
    }
    if (sympathyProducts?.length > 0) {
        return true;
    }
    return false;
};

// Get Stand alone greeting cards CI
export const getStandaloneGreetingCartInCart = createSelector(
    [
        getRecipientOrderItems,
    ],
    (recipients) => {
        let standaloneProducts = [];
        if (recipients) {
            standaloneProducts = recipients.filter((items) => (
                items.cartItems.filter(
                    (cartItem) => (
                        (
                            ['GREETINGCARD-ITEM'].includes(cartItem.itemInfo?.lineItemType)
                            || cartItem.product?.brandCode === '1036'
                        )
                        && cartItem.addons?.length > 0
                    ),
                ).length !== 0
            ));
        }
        return standaloneProducts?.length > 0;
    },
);
